import {ReactElement, useEffect, useState} from "react";
import {useWatch} from "react-hook-form";
import {useNavigate} from "react-router-dom";
import {styled} from "styled-components";

import {getContrat} from "@/Api/contrat";
import IconPrevious from "@/assets/iconPrevious.svg?react";
import {AdherentSelecteur} from "@/components/Adherent/AdherentSelecteur";
import {BlocContent} from "@/components/BlocContent/BlocContent";
import {Button} from "@/components/Button/Button";
import {BlocSuiviContrat} from "@/components/Contrat/BlocSuiviContrat";
import {ErrorBoundary} from "@/components/ErrorBoundary/ErrorBoundary";
import {FournisseurHeader} from "@/components/Fournisseur/FournisseurHeader/FournisseurHeader";
import {KpiElementList} from "@/components/Kpi/KpiElementList/KpiElementList";
import Loader from "@/components/Loader/Loader";
import {LoadingOrError} from "@/components/LoadingOrError/LoadingOrError";
import {Title} from "@/components/Title/Title";
import {AppContext} from "@/context/AppContext";
import {
  FournisseurContext,
  FournisseurContextType,
} from "@/context/FournisseurContext";
import {HookFormContext, HookFormContextType} from "@/context/HookFormContext";
import {
  UserParamContext,
  UserParamContextType,
} from "@/context/UserParamContext";
import {useGetContext} from "@/hooks/useGetContext/useGetContext";
import {useIsConnectedTest} from "@/hooks/useIsConnectedTest/useIsConnectedTest";
import {useLoadingState} from "@/hooks/useLoadingState/useLoadingState";
import {AppContext as AppContextType} from "@/ts/appContext";
import {Contrat} from "@/ts/contrat";
import {CampagneFournisseur} from "@/ts/kpi";

const FournisseurContent = styled.div`
  width: 100%;
  display: grid;
  gap: 40px;
  margin-top: 15px;
`;

// page Fournisseur qui affiche les informations du fournisseur et le contrat entre le fournisseur et l'adherent
export function Fournisseur(): ReactElement | null {
  const navigate = useNavigate();
  const {API = undefined} = useGetContext<AppContextType>(
    AppContext,
    "AppContext"
  );
  const {
    fournisseurAndCampagne,
    getFournisseur,
    fournisseurAndCampagneLoadingState,
  } = useGetContext<FournisseurContextType>(
    FournisseurContext,
    "FournisseurContext"
  );

  const contratLoadingState = useLoadingState();

  const {control} = useGetContext<HookFormContextType>(
    HookFormContext,
    "HookFormContext"
  );
  const selected_adherent = useWatch({control, name: "selected_adherent"});
  const {currentCampagne, currentContremarque} =
    useGetContext<UserParamContextType>(UserParamContext, "UserParamContext");

  const [savedCampagneAndContremarque, setSavedCampagneAndContremarque] =
    useState<{campagne: string; contremarque: string}>({
      campagne: "",
      contremarque: "",
    });

  const isAuthorized = useIsConnectedTest();
  const {kpis} =
    (fournisseurAndCampagne?.campagne as CampagneFournisseur) || [];

  const [contrat, setContrat] = useState<Contrat | null>(
    fournisseurAndCampagne?.contrat || null
  );

  useEffect(() => {
    const updateContrat = async (): Promise<void> => {
      const newContrat = await contratLoadingState.executeWithLoading(
        async () => await getContrat(API, selected_adherent.value)
      );
      setContrat(newContrat);
    };
    if (selected_adherent) {
      updateContrat();
    } else {
      setContrat(null);
    }
  }, [selected_adherent]);

  useEffect(() => {
    if (
      currentCampagne.value !== savedCampagneAndContremarque.campagne ||
      currentContremarque.value !== savedCampagneAndContremarque.contremarque
    ) {
      getFournisseur();
      setSavedCampagneAndContremarque({
        campagne: currentCampagne.value,
        contremarque: currentContremarque.value,
      });
    }
  }, [currentCampagne, currentContremarque]);

  // si l'utilisateur n'est pas connecté, on ne rend pas la page
  if (!isAuthorized) {
    return null;
  }

  return (
    <ErrorBoundary
      fallbackText="Erreur lors de la récupération des données du fournisseur"
      buttonToRefresh={true}>
      <Button label="RETOUR" icon={IconPrevious} onClick={() => navigate(-1)} />
      <FournisseurContent data-testid="FournisseurContent">
        <LoadingOrError
          loadingState={fournisseurAndCampagneLoadingState}
          errorImage="/path/to/error-image.png"
          buttonToRefresh={true}
          onRetry={async () => await getFournisseur()}
          data={fournisseurAndCampagne}
          dataValidator={data => data && data?.campagne && data?.fournisseur}>
          {/* On affiche le contenu si le fournisseur est trouvé */}
          <>
            {fournisseurAndCampagne && (
              <FournisseurHeader
                fournisseurInfo={fournisseurAndCampagne.fournisseur}
                bonus={fournisseurAndCampagne.campagne.bonus}>
                <AdherentSelecteur
                  name="selected_adherent"
                  adherents={
                    fournisseurAndCampagne.adherents_avec_contrat?.filter(
                      adherent =>
                        adherent.contrat_statut === "actif" ||
                        adherent.contrat_statut === "cloture"
                    ) || []
                  }
                  handleSelect={() => {}}
                  handleTrash={() => {}}
                />
              </FournisseurHeader>
            )}

            {contratLoadingState.isLoading ? (
              <Loader />
            ) : contrat?.statut === "actif" || contrat?.statut === "cloture" ? (
              <BlocSuiviContrat contrat={contrat} />
            ) : (
              <BlocContent>
                <Title
                  title={`KPI du programme ${
                    fournisseurAndCampagne?.fournisseur?.nom
                  }`}></Title>
                {kpis?.map(kpi => <KpiElementList kpi={kpi} />)}
              </BlocContent>
            )}
          </>
        </LoadingOrError>
      </FournisseurContent>
    </ErrorBoundary>
  );
}
